import React, {
  Children,
  ForwardedRef,
  ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {CarouselElement} from '../../../carousel/CarouselElement.interface';
import {CarouselValuesType} from '../../../carousel/CarouselValuesType';
import {SlideDirection} from '../../../carousel/SlideDirection.enum';
import {useCarouselBreakpoint} from '../../../carousel/useCarouselBreakpoint';
import {useCarouselEvents} from '../../../carousel/useCarouselEvents';
import {useCarouselMethods} from '../../../carousel/useCarouselMethods';
import {getValidChildren} from '../../../common/shared/utils/getValidChildren';
import {twMerge} from '../../../common/twMerge';
import {useIsomorphicLayoutEffect} from '../../../common/useIsomorphicLayoutEffect.hook';
import {CarouselProps} from './CarouselProps.interface';
import {useCarouselAnimation} from './useCarouselAnimation';

interface UseCarouselValuesProps extends CarouselProps {
  ref: ForwardedRef<CarouselElement | null>;
}

export function useCarouselValues({
  responsive,
  children,
  className,
  ref,
  ...carouselProps
}: UseCarouselValuesProps): CarouselValuesType {
  const [currentIndex, setCurrentIndex] = useState(0);
  const currentIndexRef = useRef(currentIndex);
  const prevIndexRef = useRef<number>();

  useIsomorphicLayoutEffect(() => {
    prevIndexRef.current = currentIndexRef.current;
    currentIndexRef.current = currentIndex;
  }, [currentIndex]);

  // TODO: (@kai) 서버사이드 렌더링의 경우 최초에 breakpoint를 구하지 못해 요소가 바로 자리를 잡지 못하는 현상이 있습니다. 추후 해결책을 찾아 수정해야 합니다.
  const breakPoints = useMemo(
    () =>
      responsive
        ? Object.keys(responsive).map(breakPoint => Number(breakPoint))
        : undefined,
    [responsive]
  );

  const currentBreakPoint = useCarouselBreakpoint(breakPoints);

  const {
    isAutoPlay = false,
    autoInterval = 2000,
    slideSpeed = 500,
    isInfinite = false,
    isSupportMouseEvent = true,
    minPxToSwipeItem = 10,
    isCenterMode = false,
    centerPadding = '10%',
    isLeftSideMode = false,
    renderDots,
    renderNextArrow,
    renderPrevArrow,
    onChangeIndex,
    gapPx,
    ...responsiveCarouselProps
  } = useMemo(() => {
    const responsiveCarouselOption =
      responsive && currentBreakPoint
        ? responsive[currentBreakPoint]
        : undefined;
    return {...carouselProps, ...responsiveCarouselOption};
  }, [carouselProps, responsive, currentBreakPoint]);

  useEffect(() => {
    onChangeIndex?.(currentIndex);
  }, [onChangeIndex, currentIndex]);

  const carouselItemCount = useMemo(() => Children.count(children), [children]);
  const lastIndex = useMemo(() => carouselItemCount - 1, [carouselItemCount]);

  // slide 넘어가는 속도가 있으므로, 이를 고려해 autoInterval값이 너무 작으면 보정해줌
  const _autoInterval = useMemo(
    () => Math.max(autoInterval, slideSpeed + 100),
    [autoInterval, slideSpeed]
  );
  // Center mode가 아니면 center padding값 무시함. 최대 33.3%까지만 지원한다
  const _centerPadding = useMemo(
    () => `min(33.3%, ${isCenterMode ? centerPadding : '0px'})`,
    [isCenterMode, centerPadding]
  );

  // 캐러셀 앞뒤로 슬라이드 아이템을 복사해 넣을 갯수. infinite 진행 시 매끄러운 동작을 위해 추가한다. (ex. A/B/C 슬라이드가 있다면 C/A/B/C/A 가 렌더된다)
  // centerPadding이 들어간 경우 한번에 2개 이상의 캐러셀 아이템이 보일 수 있어 2개를 더 추가해준다.
  // XXX: 추후 더 많은 양의 캐러셀 아이템을 한 번에 보여줄 경우 그에 맞게 보여줄 수 있어야 함
  const cloneElementCount = useMemo(
    () => (isInfinite ? (isCenterMode ? 2 : 1) : 0),
    [isCenterMode, isInfinite]
  );

  const onTransitionCarouselRef = useRef(false);
  // 캐러셀 동작과 관련있는 element
  const carouselRef = useRef<HTMLDivElement>(null);
  // 캐러셀 그 자체의 element
  const carouselWrapperRef = useRef<CarouselElement>(null);
  const slideDirectionRef = useRef<SlideDirection>(SlideDirection.None);

  const {goToNext, goToPrev, goToIndex, startAutoplay, stopAutoplay} =
    useCarouselMethods({
      lastIndex,
      slideDirectionRef,
      isInfinite,
      setCurrentIndex,
      autoInterval: _autoInterval,
    });

  useImperativeHandle<
    CarouselElement | null,
    CarouselElement | null
  >(ref, () => {
    if (!carouselWrapperRef.current) {
      return null;
    }
    carouselWrapperRef.current.goToIndex = goToIndex;
    carouselWrapperRef.current.goToNext = goToNext;
    carouselWrapperRef.current.goToPrev = goToPrev;
    return carouselWrapperRef.current;
  }, [goToNext, goToPrev, goToIndex]);

  useEffect(() => {
    if (isAutoPlay) {
      startAutoplay();
    }
    return () => stopAutoplay();
  }, [isAutoPlay, startAutoplay, stopAutoplay]);

  // 캐러셀에 붙일 이벤트 생성
  const {
    onTouchEndCarousel,
    onTouchMoveCarousel,
    onTouchStartCarousel,
    onMouseDownCarousel,
  } = useCarouselEvents({
    carouselRef,
    currentIndex,
    lastIndex,
    goToNext,
    goToPrev,
    isSupportMouseEvent,
    isInfiniteCarousel: isInfinite,
    minPxToSwipeItem,
    onTransitionCarouselRef,
    startAutoplay,
    stopAutoplay,
    isAutoplay: isAutoPlay,
    slideSpeed,
  });

  // 캐러셀 넘어갈 때 애니메이션 추가
  useCarouselAnimation({
    isLeftSideMode,
    carouselRef,
    currentIndex,
    lastIndex,
    slideSpeed,
    onTransitionCarouselRef,
    centerPadding: _centerPadding,
    cloneElementCount,
    slideDirectionRef,
  });

  const validChildren = useMemo(() => getValidChildren(children), [children]);

  const onClickNextArrow = useCallback(() => {
    // n -> 1 -> 2 번쨰 순으로 아이템이 한바퀴 돌 때, n -> 1에서 가상 아이템(n+1번쨰)을 보여줍니다.
    // 아이템 이동 간에 슬라이드 애니메이션이 있는데 애니메이션이 종료될 때 1번째 아이템으로 변경합니다.
    // 애니메이션 종료이벤트가 발생하기 전에 유저가 다음 버튼을 클릭하면 n+1 -> 2번째 아이템으로 휙 이동하는 이슈가 있습니다.
    // 이를 막기 위해, n -> 1 번째로 이동할 때 다음 버튼 동작을 막아서 애니메이션 종료이벤트 핸들러(노출되는 아이템을 n+1 -> 1번째로 변경)가 호출되도록 합니다.
    if (
      currentIndexRef.current === 0 &&
      prevIndexRef.current === lastIndex &&
      onTransitionCarouselRef.current
    ) {
      return;
    }
    if (isAutoPlay) {
      stopAutoplay();
      startAutoplay();
    }
    goToNext();
  }, [goToNext, isAutoPlay, lastIndex, startAutoplay, stopAutoplay]);

  const onClickPrevArrow = useCallback(() => {
    // onClickNextArrow와 동일한 이유로 1 -> n -> n-1 번째로 이동할 때, 이전버튼 동작을 막습니다.
    if (
      currentIndexRef.current === lastIndex &&
      prevIndexRef.current === 0 &&
      onTransitionCarouselRef.current
    ) {
      return;
    }

    if (isAutoPlay) {
      stopAutoplay();
      startAutoplay();
    }
    goToPrev();
  }, [goToPrev, isAutoPlay, lastIndex, startAutoplay, stopAutoplay]);

  const renderCarouselItem = useCallback(
    (item: ReactNode, isCloned: boolean, key?: React.Key) => {
      return (
        <div
          key={key}
          // eslint-disable-next-line tailwindcss/no-custom-classname
          className={twMerge(
            isCloned && 'carousel-item-container',
            'inline-block shrink-0 transform-gpu'
          )}
          style={{
            width: `calc(100% - calc(${_centerPadding} * 2))`,
            paddingLeft: gapPx,
            paddingRight: gapPx,
          }}
        >
          {item}
          {/* clone된 요소의 비디오는 사용하지 않는다. */}
          <style jsx>{`
            .carousel-item-container :global(video) {
              display: none;
            }
          `}</style>
        </div>
      );
    },
    [_centerPadding, gapPx]
  );

  const renderCloneItems = useCallback(
    (position: 'prev' | 'next') => {
      return validChildren.map((child, idx) => {
        const needClone =
          position === 'prev'
            ? idx >= validChildren.length - cloneElementCount
            : idx < cloneElementCount;

        return needClone
          ? renderCarouselItem(child, true, `${position}_clone_${idx}`)
          : null;
      });
    },
    [validChildren, renderCarouselItem, cloneElementCount]
  );

  const renderItems = useCallback(() => {
    return validChildren.map((child, idx) =>
      renderCarouselItem(child, false, `origin_${idx}`)
    );
  }, [validChildren, renderCarouselItem]);

  return {
    carouselRef,
    carouselWrapperRef,
    responsiveCarouselProps,
    validChildren,
    cloneElementCount,
    carouselItemCount,
    currentIndex,
    _centerPadding,
    gapPx,
    isLeftSideMode,
    onTouchEndCarousel,
    onTouchMoveCarousel,
    onTouchStartCarousel,
    onMouseDownCarousel,
    onClickNextArrow,
    onClickPrevArrow,
    renderCloneItems,
    renderItems,
    renderNextArrow,
    renderPrevArrow,
    renderDots,
  };
}
